import { Ref, useEffect } from 'react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DynamicFieldRenderer } from '@/shared/components/form';
import { Input } from '@/shared/components/ui/input';
import { useSurveySubmit } from '../hooks/useSurveySubmit';
import type { OnboardingStep } from '../types/onboarding-types';

interface SurveyStepProps {
  setCurrentStep: (step: OnboardingStep) => void;
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  surveyConfig: any;
  formRef: Ref<HTMLFormElement>;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  onStepCompleted?: () => void;
  finishAfterSurvey?: boolean;
}

export const SurveyStep = ({ setCurrentStep, surveyConfig, formRef, onSubmittingChange, onStepCompleted, finishAfterSurvey = false }: SurveyStepProps) => {
  const { t } = useTranslation();

  const {
    onSubmit,
    isSubmitting,
    error,
    surveyData,
    surveyErrors,
    handleFieldChange,
    functionValue,
    functionError,
    handleFunctionChange,
  } = useSurveySubmit(setCurrentStep, surveyConfig, onStepCompleted, finishAfterSurvey);
  
  // Notificar mudanças no estado de submitting
  useEffect(() => {
    if (onSubmittingChange) {
      onSubmittingChange(isSubmitting);
    }
  }, [isSubmitting, onSubmittingChange]);

  return (
    <div className="w-full max-w-2xl space-y-6 animate-fade-in">
      <div className="flex justify-center">
        <img 
          src="/assets/logo.png" 
          alt="Prodgy Logo" 
          className="h-16 w-auto"
        />
      </div>
      
      {/* Title outside card */}
      <div className="text-center px-4 space-y-2">
        <h1 className="text-2xl font-medium tracking-tight">
          {t("auth.onboarding.additionalInfo.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("auth.onboarding.additionalInfo.description")}
        </p>
      </div>
      
      <Card className="w-full shadow-lg">
        <CardContent className="px-8 py-8">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("auth.onboarding.errors.title")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form ref={formRef} onSubmit={onSubmit} className="space-y-6">
            {/* Campo Função - movido do ProfileStep */}
            <div className="space-y-2">
              <label htmlFor="function" className="text-sm font-medium">
                {t("auth.onboarding.profile.functionLabel")} <span className="text-destructive">*</span>
              </label>
              <Input
                id="function"
                value={functionValue}
                onChange={(e) => handleFunctionChange(e.target.value)}
                placeholder={t("auth.onboarding.profile.functionPlaceholder")}
                className={`h-11 ${functionError ? 'border-destructive' : ''}`}
                disabled={isSubmitting}
                data-field-error={functionError ? 'true' : 'false'}
              />
              {functionError && (
                <p className="text-sm text-destructive">{functionError}</p>
              )}
            </div>

            {/* Campos dinâmicos do survey */}
            {surveyConfig?.fields && surveyConfig.fields.length > 0 && (
                 // eslint-disable-next-line @typescript-eslint/no-explicit-any
              surveyConfig.fields.map((field: any) => (
                <DynamicFieldRenderer
                  key={field.id}
                  field={field}
                  value={surveyData[field.id]}
                  onChange={handleFieldChange}
                  disabled={isSubmitting}
                  error={surveyErrors[field.id]}
                />
              ))
            )}
          </form>
        </CardContent>
      </Card>
      
      {/* Mensagem de erro geral */}
      <div className={`text-xs text-center -mt-4 h-4 transition-opacity ${
        Object.keys(surveyErrors).length > 0 
          ? 'text-destructive opacity-100' 
          : 'opacity-0'
      }`}>
        {t('auth.onboarding.validation.requiredFieldsError')}
      </div>

    </div>
  );
};
